import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getApiBase } from './Config';
import { getAccessToken, handleAuthFailure } from './auth';
import QuestionnaireTab from './QuestionnaireTab';
import DiscountCodesTab from './DiscountCodesTab';
import TrialCouponsTab from './TrialCouponsTab';
import './dashboard.css';

function Dashboard() {
  // State for managing active tab
  const [activeTab, setActiveTab] = useState('home');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // States for data loading
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [error, setError] = useState(null);

  // Device orders
  const [deviceOrders, setDeviceOrders] = useState([]);
  const [deviceOrdersFilter, setDeviceOrdersFilter] = useState('order_placed'); // 'order_placed' | 'shipped'
  const [isLoadingDeviceOrders, setIsLoadingDeviceOrders] = useState(false);
  const [deviceOrdersError, setDeviceOrdersError] = useState(null);
  const [shippedModalOrder, setShippedModalOrder] = useState(null);
  const [shippedForm, setShippedForm] = useState({ tracking_number: '', tracking_url: '', carrier: '' });
  const [isSubmittingShipped, setIsSubmittingShipped] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [isSendingNewsletter, setIsSendingNewsletter] = useState(false);
  const [newsletterHistory, setNewsletterHistory] = useState([]);
  const [isLoadingNewsletterHistory, setIsLoadingNewsletterHistory] = useState(false);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterSubscribersTotal, setNewsletterSubscribersTotal] = useState(0);
  const [isLoadingNewsletterSubscribers, setIsLoadingNewsletterSubscribers] = useState(false);
  const [newsletterSearch, setNewsletterSearch] = useState('');
  const [contactPending, setContactPending] = useState([]);
  const [contactHistory, setContactHistory] = useState([]);
  const [isLoadingContactPending, setIsLoadingContactPending] = useState(false);
  const [isLoadingContactHistory, setIsLoadingContactHistory] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactResponseText, setContactResponseText] = useState('');
  const [isSendingContactResponse, setIsSendingContactResponse] = useState(false);
  
  // State for doctor form
  const [doctorForm, setDoctorForm] = useState({
    name: '',
    qualified_name: '',
    speciality: '',
    rating: 0,
    focus: '',
    about: '',
    number: ''
  });
  // State for dropdown menu
  const [showDropdown, setShowDropdown] = useState(false);

  // Get user data from cookies
  const userName = Cookies.get('user_name');
  const userEmail = Cookies.get('user_email');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDoctorForm({
      ...doctorForm,
      [name]: name === 'rating' ? parseFloat(value) : value
    });
  };

  
  // Fetch doctors data
  const fetchDoctors = async () => {
    setIsLoadingDoctors(true);
    setError(null);
    const accessToken = getAccessToken();
    if (!accessToken) {
      setError('You are not authenticated. Please log in again.');
      setIsLoadingDoctors(false);
      return;
    }
    
    try {
      const response = await fetch(`${getApiBase()}/admin/doctors`, {
        headers:{
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (handleAuthFailure(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch doctors');
      }
      
      const data = await response.json();
      setDoctors(data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setError('Failed to load doctors data. Please try again later.');
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Fetch patients data
  const fetchPatients = async () => {
    setIsLoadingPatients(true);
    setError(null);
    
    const accessToken = getAccessToken();
    
    if (!accessToken) {
      setError('You are not authenticated. Please log in again.');
      setIsLoadingPatients(false);
      return;
    }
    
    try {
      const response = await fetch(`${getApiBase()}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (handleAuthFailure(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch patients');
      }
      
      const data = await response.json();
      setPatients(data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError('Failed to load patients data. Please try again later.');
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Delete patient
  const handleDeletePatient = async (patientId) => {
    if (!window.confirm('Are you sure you want to delete this patient?')) {
      return;
    }
    
    const accessToken = Cookies.get('access_token');
    
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBase()}/admin/users/${patientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete patient');
      }
      
      // Remove patient from state
      setPatients(patients.filter(patient => patient.id !== patientId));
      alert('Patient deleted successfully!');
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert(`Failed to delete patient: ${error.message}`);
    }
  };
  
  // Delete doctor
  const handleDeleteDoctor = async (doctorId) => {
    
    if (!window.confirm('Are you sure you want to delete this doctor?')) {
      return;
    }
    
    const accessToken = Cookies.get('access_token');
    
    console.log(accessToken)
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBase()}/admin/doctors/${doctorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete doctor');
      }
      
      // Remove doctor from state
      setDoctors(doctors.filter(doctor => doctor.id !== doctorId));
      alert('Doctor deleted successfully!');
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert(`Failed to delete doctor: ${error.message}`);
    }
  };
  
  // Toggle doctor visibility status
  const handleToggleDoctorStatus = async (doctorId, currentVisibility) => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    
    
    // Set new visibility status (opposite of current)
    const newVisibility = !currentVisibility;
    
    try {
      const response = await fetch(`${getApiBase()}/admin/doctors/${doctorId}/visibility?visibility=${newVisibility}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to update doctor visibility');
      }
      
      // Update doctors state with new visibility status
      setDoctors(doctors.map(doctor => 
        doctor.id === doctorId ? {...doctor, visible: newVisibility} : doctor
      ));
      
      alert(`Doctor ${newVisibility ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error updating doctor visibility:', error);
      alert(`Failed to update doctor visibility: ${error.message}`);
    }
  };

  // Fetch device orders
  const fetchDeviceOrders = async (status) => {
    setIsLoadingDeviceOrders(true);
    setDeviceOrdersError(null);
    const accessToken = getAccessToken();
    if (!accessToken) {
      setDeviceOrdersError('Not authenticated.');
      setIsLoadingDeviceOrders(false);
      return;
    }
    try {
      const url = status ? `${getApiBase()}/admin/device-orders?status=${status}` : `${getApiBase()}/admin/device-orders`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (handleAuthFailure(response)) return;
      if (!response.ok) throw new Error('Failed to fetch device orders');
      const data = await response.json();
      setDeviceOrders(Array.isArray(data) ? data.map(o => ({ ...o, orderId: o.display_id || o.orderId || `order-${o.id}` })) : []);
    } catch (err) {
      console.error(err);
      setDeviceOrdersError('Failed to load device orders.');
    } finally {
      setIsLoadingDeviceOrders(false);
    }
  };

  const handleMarkShippedSubmit = async (e) => {
    e.preventDefault();
    if (!shippedModalOrder?.orderId) return;
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('Not authenticated.');
      return;
    }
    setIsSubmittingShipped(true);
    try {
      const response = await fetch(`${getApiBase()}/admin/device-orders/${encodeURIComponent(shippedModalOrder.orderId)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          status: 'shipped',
          tracking_number: shippedForm.tracking_number || null,
          tracking_url: shippedForm.tracking_url || null,
          carrier: shippedForm.carrier || null
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = typeof err.detail === 'string' ? err.detail : Array.isArray(err.detail) ? (err.detail[0]?.msg || err.detail[0] || 'Invalid request') : 'Failed to update order';
        throw new Error(msg);
      }
      setShippedModalOrder(null);
      setShippedForm({ tracking_number: '', tracking_url: '', carrier: '' });
      fetchDeviceOrders(deviceOrdersFilter);
      alert('Order marked as shipped.');
    } catch (err) {
      alert(err.message || 'Failed to update order.');
    } finally {
      setIsSubmittingShipped(false);
    }
  };

  const fetchNewsletterHistory = async () => {
    setIsLoadingNewsletterHistory(true);
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      setIsLoadingNewsletterHistory(false);
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/admin/newsletter/campaigns?limit=100`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch newsletter history');
      const data = await response.json();
      setNewsletterHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      alert('Failed to load newsletter history.');
    } finally {
      setIsLoadingNewsletterHistory(false);
    }
  };

  const fetchNewsletterSubscribers = async (search = '') => {
    setIsLoadingNewsletterSubscribers(true);
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      setIsLoadingNewsletterSubscribers(false);
      return;
    }
    try {
      const query = new URLSearchParams({ limit: '500' });
      if (search.trim()) query.set('search', search.trim());
      const response = await fetch(`${getApiBase()}/admin/newsletter/subscribers?${query.toString()}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch newsletter subscribers');
      const data = await response.json();
      setNewsletterSubscribers(Array.isArray(data.items) ? data.items : []);
      setNewsletterSubscribersTotal(Number.isFinite(data.total) ? data.total : 0);
    } catch (err) {
      console.error(err);
      alert('Failed to load newsletter subscribers.');
    } finally {
      setIsLoadingNewsletterSubscribers(false);
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterSubject.trim() || !newsletterContent.trim()) {
      alert('Please provide both subject and newsletter content.');
      return;
    }
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }

    setIsSendingNewsletter(true);
    try {
      const response = await fetch(`${getApiBase()}/admin/newsletter/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          subject: newsletterSubject.trim(),
          content: newsletterContent.trim()
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to send newsletter');
      }
      alert('Newsletter sent successfully.');
      setNewsletterSubject('');
      setNewsletterContent('');
      await Promise.all([fetchNewsletterHistory(), fetchNewsletterSubscribers(newsletterSearch)]);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to send newsletter.');
    } finally {
      setIsSendingNewsletter(false);
    }
  };

  const handleUnsubscribeNewsletter = async (subscriber) => {
    if (!subscriber?.id) return;
    if (!window.confirm(`Unsubscribe ${subscriber.email}?`)) return;
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/admin/newsletter/subscribers/${subscriber.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to unsubscribe');
      }
      setNewsletterSubscribers((prev) => prev.filter((item) => item.id !== subscriber.id));
      setNewsletterSubscribersTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to unsubscribe.');
    }
  };

  const fetchContactPending = async () => {
    setIsLoadingContactPending(true);
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      setIsLoadingContactPending(false);
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/admin/contact/pending?limit=200`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to load pending contacts');
      const data = await response.json();
      setContactPending(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      alert('Failed to load pending contact queries.');
    } finally {
      setIsLoadingContactPending(false);
    }
  };

  const fetchContactHistory = async () => {
    setIsLoadingContactHistory(true);
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      setIsLoadingContactHistory(false);
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/admin/contact/history?limit=200`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to load contact history');
      const data = await response.json();
      setContactHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      alert('Failed to load contact history.');
    } finally {
      setIsLoadingContactHistory(false);
    }
  };

  const handleOpenContact = async (queryId) => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/admin/contact/${queryId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch contact details');
      const data = await response.json();
      setSelectedContact(data);
      setContactResponseText('');
    } catch (err) {
      console.error(err);
      alert('Failed to load contact details.');
    }
  };

  const handleSendContactResponse = async (e) => {
    e.preventDefault();
    if (!selectedContact?.id) return;
    if (!contactResponseText.trim()) {
      alert('Please type a response before sending.');
      return;
    }
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      return;
    }
    setIsSendingContactResponse(true);
    try {
      const response = await fetch(`${getApiBase()}/admin/contact/${selectedContact.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ response_message: contactResponseText.trim() })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to send response');
      }
      setSelectedContact(null);
      setContactResponseText('');
      await Promise.all([fetchContactPending(), fetchContactHistory()]);
      alert('Response sent successfully.');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to send response.');
    } finally {
      setIsSendingContactResponse(false);
    }
  };

  // Handle tab changes and load appropriate data
  useEffect(() => {
    if (activeTab === 'view-doctors') {
      fetchDoctors();
    } else if (activeTab === 'view-patients') {
      fetchPatients();
    } else if (activeTab === 'device-orders') {
      fetchDeviceOrders(deviceOrdersFilter);
    } else if (activeTab === 'newsletter') {
      fetchNewsletterHistory();
      fetchNewsletterSubscribers(newsletterSearch);
    } else if (activeTab === 'contact-us') {
      fetchContactPending();
      fetchContactHistory();
    }
  }, [activeTab, deviceOrdersFilter, newsletterSearch]);

  useEffect(() => {
    if (activeTab !== 'newsletter') return;
    const handle = setTimeout(() => {
      fetchNewsletterSubscribers(newsletterSearch);
    }, 250);
    return () => clearTimeout(handle);
  }, [newsletterSearch, activeTab]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get access token from cookies
    const accessToken = Cookies.get('access_token');
    
    if (!accessToken) {
      alert('You are not authenticated. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${getApiBase()}/admin/doctors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(doctorForm),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add doctor');
      }
      
      const data = await response.json();
      console.log('Doctor added successfully:', data);
      alert('Doctor added successfully!');
      
      // Reset form
      setDoctorForm({
        name: '',
        qualified_name: '',
        speciality: '',
        rating: 0,
        focus: '',
        about: '',
        number: ''
      });
    } catch (error) {
      console.error('Error adding doctor:', error);
      alert(`Failed to add doctor: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle sign out
  const handleSignOut = () => {
    // Remove all cookies
    Object.keys(Cookies.get()).forEach(cookieName => {
      Cookies.remove(cookieName);
    });
    // Redirect to login page or refresh the page
    window.location.href = '/login';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatGender = (gender) => {
    if (!gender) return '—';
    const labels = {
      male: 'Male',
      female: 'Female',
      other: 'Other',
      prefer_not_to_say: 'Prefer not to say',
    };
    return labels[gender] || gender;
  };

  const renderDeviceOrderPrice = (order) => {
    const paid = order.amount_paid_inr;
    if (paid == null || paid === 0) {
      return <span className="text-gray-400">—</span>;
    }
    const listInr =
      order.list_price_paise != null ? Math.round(order.list_price_paise / 100) : null;
    const showListPrice = listInr != null && listInr > paid;

    return (
      <div className="leading-snug">
        {showListPrice && (
          <div className="text-xs text-gray-400 line-through">
            ₹{listInr.toLocaleString('en-IN')}
          </div>
        )}
        <div className="font-medium text-gray-900">₹{paid.toLocaleString('en-IN')}</div>
        {order.promo_code && (
          <div className="text-xs text-green-700 mt-0.5">
            {order.promo_code}
            {order.discount_percent != null ? ` (${order.discount_percent}% off)` : ''}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Nidra Dashboard</h1>
            <div className="flex items-center space-x-4">
              <div className="text-right mr-4">
                <p className="text-sm text-gray-600">Welcome, {userName || 'User'}</p>
                <p className="text-xs text-gray-500">Email: {userEmail || 'Admin'}</p>
              </div>
              <div className="relative">
                <button 
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                </button>
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-start">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('home')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === 'home' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => setActiveTab('add-doctor')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'add-doctor' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Add Doctor
              </button>
              <button
                onClick={() => setActiveTab('view-doctors')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'view-doctors' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                View Doctors
              </button>
              <button
                onClick={() => setActiveTab('view-patients')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'view-patients' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                View Patients
              </button>
              <button
                onClick={() => setActiveTab('device-orders')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'device-orders' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Device Orders
              </button>
              <button
                onClick={() => setActiveTab('discount-codes')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'discount-codes' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Discount codes
              </button>
              <button
                onClick={() => setActiveTab('trial-coupons')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'trial-coupons' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Trial coupons
              </button>
              <button
                onClick={() => setActiveTab('questionnaire')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'questionnaire' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Questionnaire
              </button>
              <button
                onClick={() => setActiveTab('newsletter')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'newsletter' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Newsletter
              </button>
              <button
                onClick={() => setActiveTab('contact-us')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'contact-us' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
          {activeTab === 'home' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Welcome to the Nidra Dashboard</h2>
              <p>Select an option from the navigation menu to manage doctors and patients.</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-medium text-blue-800">Add Doctors</h3>
                  <p className="mt-2 text-sm text-blue-600">Register new doctors to the system with their specialties.</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-medium text-green-800">View Doctors</h3>
                  <p className="mt-2 text-sm text-green-600">View and manage all doctors in the system.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="font-medium text-purple-800">View Patients</h3>
                  <p className="mt-2 text-sm text-purple-600">View and manage patient records.</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h3 className="font-medium text-amber-800">Device Orders</h3>
                  <p className="mt-2 text-sm text-amber-600">View EEG device orders and mark as shipped.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'add-doctor' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Add New Doctor</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={doctorForm.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="qualified_name" className="block text-sm font-medium text-gray-700">Qualified Name</label>
                    <input
                      type="text"
                      name="qualified_name"
                      id="qualified_name"
                      value={doctorForm.qualified_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="speciality" className="block text-sm font-medium text-gray-700">Speciality</label>
                    <input
                      type="text"
                      name="speciality"
                      id="speciality"
                      value={doctorForm.speciality}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="rating" className="block text-sm font-medium text-gray-700">Rating (0-5)</label>
                    <input
                      type="number"
                      name="rating"
                      id="rating"
                      min="0"
                      max="5"
                      step="0.1"
                      value={doctorForm.rating}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="focus" className="block text-sm font-medium text-gray-700">Focus Area</label>
                    <input
                      type="text"
                      name="focus"
                      id="focus"
                      value={doctorForm.focus}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="number" className="block text-sm font-medium text-gray-700">Contact Number</label>
                    <input
                      type="text"
                      name="number"
                      id="number"
                      value={doctorForm.number}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="about" className="block text-sm font-medium text-gray-700">About</label>
                  <textarea
                    name="about"
                    id="about"
                    rows="4"
                    value={doctorForm.about}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  ></textarea>
                </div>
                <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300"
                >
                  {isSubmitting ? 'Adding...' : 'Add Doctor'}
                </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'view-doctors' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Doctors List</h2>
                <button
                  onClick={() => setActiveTab('add-doctor')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Add Doctor
                </button>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {isLoadingDoctors ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {doctors.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qualified Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Speciality</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile Number</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Focus</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">About</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {doctors.map((doctor) => (
                          <tr key={doctor.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doctor.id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{doctor.qualified_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doctor.speciality}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <span className="text-yellow-500 mr-1">★</span>
                                {doctor.rating}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doctor.number}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doctor.focus}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                doctor.visible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {doctor.visible ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{doctor.about}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-3">
                                <button
                                  onClick={() => handleToggleDoctorStatus(doctor.id, doctor.visible)}
                                  className={`${doctor.visible ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} focus:outline-none`}
                                  title={doctor.visible ? "Disable Doctor" : "Enable Doctor"}
                                >
                                  {doctor.visible ? (
                                    // Disable icon
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    // Enable icon
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteDoctor(doctor.id)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none"
                                  title="Delete Doctor"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="bg-gray-100 p-6 rounded-lg text-center">
                      <p className="text-gray-500">No doctors found in the system.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'device-orders' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Device Orders</h2>
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setDeviceOrdersFilter('order_placed')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${deviceOrdersFilter === 'order_placed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Not shipped
                </button>
                <button
                  onClick={() => setDeviceOrdersFilter('shipped')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${deviceOrdersFilter === 'shipped' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Shipped
                </button>
              </div>
              {deviceOrdersError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{deviceOrdersError}</div>
              )}
              {isLoadingDeviceOrders ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="device-orders-scroll">
                  {deviceOrders.length > 0 ? (
                    <table className="device-orders-table divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          {deviceOrdersFilter === 'shipped' && (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipped at</th>
                            </>
                          )}
                          {deviceOrdersFilter === 'order_placed' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deviceOrders.map((order) => (
                          <tr key={order.orderId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.orderId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.patient_id}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 cell-wrap">
                              {[order.address_line1, order.address_line2, order.city, order.state, order.pincode].filter(Boolean).join(', ')}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 cell-wrap">{order.contact_name} / {order.contact_phone}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{renderDeviceOrderPrice(order)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(order.created_at)}</td>
                            {deviceOrdersFilter === 'shipped' && (
                              <>
                                <td className="px-6 py-4 text-sm text-gray-500 cell-wrap">
                                  {order.tracking_number && <span>#{order.tracking_number}</span>}
                                  {order.tracking_url && (
                                    <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 ml-1">Track</a>
                                  )}
                                  {order.carrier && <span className="ml-1">({order.carrier})</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(order.shipped_at)}</td>
                              </>
                            )}
                            {deviceOrdersFilter === 'order_placed' && (
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => { setShippedModalOrder(order); setShippedForm({ tracking_number: '', tracking_url: '', carrier: '' }); }}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Mark as shipped
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="bg-gray-100 p-6 rounded-lg text-center text-gray-500">No orders found.</div>
                  )}
                </div>
              )}
              {shippedModalOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isSubmittingShipped && setShippedModalOrder(null)}>
                  <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold mb-2">Mark as shipped: {shippedModalOrder.orderId}</h3>
                    <form onSubmit={handleMarkShippedSubmit} className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tracking number</label>
                        <input
                          type="text"
                          value={shippedForm.tracking_number}
                          onChange={e => setShippedForm(f => ({ ...f, tracking_number: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tracking URL</label>
                        <input
                          type="url"
                          value={shippedForm.tracking_url}
                          onChange={e => setShippedForm(f => ({ ...f, tracking_url: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Carrier</label>
                        <input
                          type="text"
                          value={shippedForm.carrier}
                          onChange={e => setShippedForm(f => ({ ...f, carrier: e.target.value }))}
                          placeholder="e.g. Bluedart, DTDC"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        />
                      </div>
                      <div className="flex justify-end space-x-2 pt-2">
                        <button type="button" onClick={() => setShippedModalOrder(null)} disabled={isSubmittingShipped} className="px-4 py-2 border border-gray-300 rounded-md text-sm">Cancel</button>
                        <button type="submit" disabled={isSubmittingShipped} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50">{isSubmittingShipped ? 'Saving...' : 'Save'}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'discount-codes' && <DiscountCodesTab />}

          {activeTab === 'trial-coupons' && <TrialCouponsTab />}

          {activeTab === 'questionnaire' && <QuestionnaireTab />}

          {activeTab === 'newsletter' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Newsletter</h2>

              <form onSubmit={handleSendNewsletter} className="space-y-4 mb-4">
                <div>
                  <label htmlFor="newsletter-subject" className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    id="newsletter-subject"
                    type="text"
                    value={newsletterSubject}
                    onChange={(e) => setNewsletterSubject(e.target.value)}
                    placeholder="Enter newsletter subject"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="newsletter-content" className="block text-sm font-medium text-gray-700">Newsletter content</label>
                  <textarea
                    id="newsletter-content"
                    rows="6"
                    value={newsletterContent}
                    onChange={(e) => setNewsletterContent(e.target.value)}
                    placeholder="Type newsletter content"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  ></textarea>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSendingNewsletter}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300"
                  >
                    {isSendingNewsletter ? 'Sending...' : 'Send Newsletter'}
                  </button>
                </div>
              </form>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">History</h3>
                  </div>
                  {isLoadingNewsletterHistory ? (
                    <p className="text-gray-500">Loading history...</p>
                  ) : newsletterHistory.length === 0 ? (
                    <p className="text-gray-500">No newsletter history yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent at</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {newsletterHistory.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-900">{item.subject}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(item.sent_at || item.created_at)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.delivered_count}/{item.total_recipients}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Subscribers ({newsletterSubscribersTotal})</h3>
                  </div>
                  <div className="mb-4">
                    <input
                      type="text"
                      value={newsletterSearch}
                      onChange={(e) => setNewsletterSearch(e.target.value)}
                      placeholder="Search by email"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  {isLoadingNewsletterSubscribers ? (
                    <p className="text-gray-500">Loading subscribers...</p>
                  ) : newsletterSubscribers.length === 0 ? (
                    <p className="text-gray-500">No subscribers found.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined at</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {newsletterSubscribers.map((subscriber) => (
                            <tr key={subscriber.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-900">{subscriber.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(subscriber.joined_at)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() => handleUnsubscribeNewsletter(subscriber)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none"
                                  title="Unsubscribe"
                                  aria-label={`Unsubscribe ${subscriber.email}`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact-us' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-xl font-semibold mb-3">Pending</h3>
                  {isLoadingContactPending ? (
                    <p className="text-gray-500">Loading pending queries...</p>
                  ) : contactPending.length === 0 ? (
                    <p className="text-gray-500">No pending contact queries.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {contactPending.map((item) => (
                            <tr
                              key={item.id}
                              className="hover:bg-gray-50"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleOpenContact(item.id)}
                            >
                              <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{item.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(item.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-xl font-semibold mb-3">History</h3>
                  {isLoadingContactHistory ? (
                    <p className="text-gray-500">Loading history...</p>
                  ) : contactHistory.length === 0 ? (
                    <p className="text-gray-500">No responded queries yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responded</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {contactHistory.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{item.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(item.responded_at || item.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {selectedContact && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isSendingContactResponse && setSelectedContact(null)}>
                  <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold mb-2">Respond to Contact Query</h3>
                    <p className="text-sm text-gray-700"><strong>Name:</strong> {selectedContact.name}</p>
                    <p className="text-sm text-gray-700"><strong>Email:</strong> {selectedContact.email}</p>
                    <p className="text-sm text-gray-700 mt-2"><strong>Query:</strong></p>
                    <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 mt-1">{selectedContact.message}</p>

                    <form onSubmit={handleSendContactResponse} className="space-y-3 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Response</label>
                        <textarea
                          rows="5"
                          value={contactResponseText}
                          onChange={(e) => setContactResponseText(e.target.value)}
                          placeholder="Type your response"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          required
                        ></textarea>
                      </div>
                      <div className="flex justify-end space-x-2 pt-2">
                        <button type="button" onClick={() => setSelectedContact(null)} disabled={isSendingContactResponse} className="px-4 py-2 border border-gray-300 rounded-md text-sm">Cancel</button>
                        <button type="submit" disabled={isSendingContactResponse} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50">
                          {isSendingContactResponse ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'view-patients' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Patients List</h2>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {isLoadingPatients ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {patients.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date of Birth</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {patients.map((patient) => (
                          <tr key={patient.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{patient.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.number}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.country}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatGender(patient.gender)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(patient.dob)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center gap-3">
                                <Link
                                  to={`/patients/${patient.id}/report`}
                                  className="text-blue-600 hover:text-blue-900 focus:outline-none"
                                  title="View patient report"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                  </svg>
                                </Link>
                                <button
                                  onClick={() => handleDeletePatient(patient.id)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none"
                                  title="Delete Patient"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="bg-gray-100 p-6 rounded-lg text-center">
                      <p className="text-gray-500">No patients found in the system.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;